'use client';
import {
  CatImageGallery,
  CatImageGalleryItem,
  CatImageGalleryThumbnail,
  CatImageGalleryThumbnailItem
} from 'components/blocks-components';
import {
  PRODUCT_FALLBACK_IMAGE,
  PAGE_BREAKPOINTS,
  ONETRUST
} from 'constants/common';
import Image from 'next/image';
import { VIDEO } from '../../constants/constants';
import ProductImageGalleryZoomButton from './product-image-gallery-zoom-button';
import { Fragment, useEffect, useState } from 'react';
import ProductImageZoomDrawer, {
  ZoomGalleryItems
} from './product-image-zoom-drawer/product-image-zoom-drawer';
import { v4 as uuidv4 } from 'uuid';
import cx from 'classnames';
import { isEmpty } from '@cat-ecom/common/utils/commonUtils';
import styles from '../../product-page.module.scss';
import { Conditional } from '@cat-ecom/pcc-components';
import { OverlayMessage } from 'components/OverlayMessage';
import VideoWarning from 'components/video-warning';
import { isConsentTypeAvailable } from 'utils/oneTrustUtils';
import MagnifyImage from '../magnify-image';
import { GalleryItem } from '../../types';

const ProductImagePicker = ({
  galleryItems,
  thumbnailsImagesFlag,
  isDiscontinued = false,
  isHeadlessPdpFollowUp,
  hideThumbnails = false
}: {
  galleryItems: GalleryItem[];
  thumbnailsImagesFlag: boolean;
  isDiscontinued?: boolean;
  isHeadlessPdpFollowUp: boolean;
  hideThumbnails?: boolean;
}) => {
  const [drawerActive, setDrawerActive] = useState(false);
  const [selectedImgIndex, setSelectedImgIndex] = useState(0);
  const [showWarning, setShowWarning] = useState(false);

  useEffect(() => {
    const cookiesEnableCheck = () => {
      if (isConsentTypeAvailable(ONETRUST.TARGET_COOKIES_ACCEPTED)) {
        setShowWarning(false);
      } else {
        setShowWarning(true);
      }
    };
    cookiesEnableCheck();

    window.addEventListener('OneTrustGroupsUpdated', cookiesEnableCheck);

    return () => {
      window.removeEventListener('OneTrustGroupsUpdated', cookiesEnableCheck);
    };
  }, []);

  const showVideoWarning = (src = '') => (
    <CatImageGalleryItem
      className={cx(styles['product-image__iframe'], 'w-100')}
    >
      {showWarning && isHeadlessPdpFollowUp ? (
        <VideoWarning isproduct />
      ) : (
        <iframe
          src={`${src}?controls=1&embedded=true`}
          loading="lazy"
          className="w-100"
          title={uuidv4()}
        />
      )}
    </CatImageGalleryItem>
  );

  const isFallbackImage =
    isEmpty(galleryItems) ||
    (galleryItems.length === 1 &&
      galleryItems[0]?.thumbnailSrc === PRODUCT_FALLBACK_IMAGE);

  const mobileBreakPoint = thumbnailsImagesFlag ? 0 : PAGE_BREAKPOINTS.MD;

  const handleCloseDrawer = () => {
    setDrawerActive(false);
  };

  const handleOpenDrawer = (imageIndex: number) => {
    setDrawerActive(true);
    setSelectedImgIndex(imageIndex);
  };

  const showPartImage = (image, index: number) => {
    const zoomButton = (
      <ProductImageGalleryZoomButton
        image={image}
        openDrawer={() => handleOpenDrawer(index)}
        thumbnailsImagesFlag={thumbnailsImagesFlag}
        isDiscontinued={isDiscontinued}
      />
    );

    return isHeadlessPdpFollowUp ? (
      <MagnifyImage>{zoomButton}</MagnifyImage>
    ) : (
      zoomButton
    );
  };

  return (
    <div className={`cat-u-margin-top-lg ${styles['product-image__size']}`}>
      <Conditional test={isFallbackImage}>
        <div className={styles['product-fallback-image-container']}>
          {isHeadlessPdpFollowUp ? (
            <MagnifyImage
              key={`product-image-picker-${PRODUCT_FALLBACK_IMAGE}`}
              isFallbackImage={isFallbackImage}
            >
              <Image
                src={PRODUCT_FALLBACK_IMAGE}
                alt="fallback-image"
                width={343}
                height={343}
                unoptimized
                style={{ opacity: isDiscontinued ? '70%' : '100%' }}
              />
            </MagnifyImage>
          ) : (
            <Image
              src={PRODUCT_FALLBACK_IMAGE}
              alt="fallback-image"
              width={343}
              height={343}
              unoptimized
              style={{ opacity: isDiscontinued ? '70%' : '100%' }}
            />
          )}

          {isDiscontinued && <OverlayMessage text={'DISCONTINUED_OVERLAY'} />}
        </div>
      </Conditional>
      <Conditional test={!isFallbackImage}>
        <CatImageGallery
          gridPosition="bottom"
          className={styles['product-icon-image-grid']}
          mobileBreakPoint={mobileBreakPoint}
          hideThumbnails={hideThumbnails}
        >
          <CatImageGalleryThumbnail
            className={cx({
              'd-none d-md-block mt-0': hideThumbnails
            })}
            data-testid="product-image-picker-thumbnails"
          >
            {galleryItems.map((image) => (
              <CatImageGalleryThumbnailItem
                key={image.id}
                src={image.thumbnailSrc ?? image.src}
                alt={image.alt}
              />
            ))}
          </CatImageGalleryThumbnail>
          {galleryItems.map((image, index: number) => (
            <Fragment key={image.id}>
              {image.type === VIDEO
                ? showVideoWarning(image.src)
                : showPartImage(image, index)}
            </Fragment>
          ))}
        </CatImageGallery>

        <ProductImageZoomDrawer
          drawerActive={drawerActive}
          closeDrawer={handleCloseDrawer}
          currentImageIndex={selectedImgIndex}
          galleryImages={galleryItems as ZoomGalleryItems[]}
        />
      </Conditional>
    </div>
  );
};

export default ProductImagePicker;
